import React, { useState, useEffect } from 'react';
import { StudentCounselModel, MemberModel } from '../../../../models';
import { User, CheckCircle2, Clock, Search } from 'lucide-react';
import Pagination from '../../../../components/ui/Pagination';

interface CounselListProps {
    counsels: StudentCounselModel[]; // Already filtered
    members: MemberModel[];
    onSelect: (counsel: StudentCounselModel) => void;
    selectedId?: string;
}

const CounselList: React.FC<CounselListProps> = ({ counsels = [], members = [], onSelect, selectedId }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 10;

    const totalPages = Math.ceil(counsels.length / ITEMS_PER_PAGE);
    const paginatedCounsels = counsels.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    // Reset pagination when the filtered list changes
    useEffect(() => {
        setCurrentPage(1);
    }, [counsels]);

    // Helper to get member name
    const getMemberName = (memberId: string) => {
        const member = members.find(m => m.id === memberId);
        return member ? member.name : '알 수 없음';
    };

    // Helper to get member grade
    const getMemberGrade = (memberId: string) => {
        const member = members.find(m => m.id === memberId);
        return member ? member.grade : '';
    };

    return (
        <div className="flex flex-col h-full bg-white border-r border-slate-200">
            <div className="flex-1 overflow-y-auto">
                <div className="divide-y divide-slate-100">
                    {paginatedCounsels.map((counsel) => (
                        <div
                            key={counsel.id}
                            onClick={() => onSelect(counsel)}
                            className={`p-5 cursor-pointer transition-all hover:bg-slate-50 group ${selectedId === counsel.id ? 'bg-indigo-50/50 hover:bg-indigo-50 border-l-4 border-indigo-500' : 'border-l-4 border-transparent'}`}
                        >
                            <div className="flex justify-between items-start mb-2">
                                <div className="flex items-center gap-2">
                                    <h4 className={`font-bold text-[15px] ${selectedId === counsel.id ? 'text-indigo-700' : 'text-slate-800'}`}>
                                        {counsel.title}
                                    </h4>
                                    {counsel.reply ? (
                                        <CheckCircle2 size={14} className="text-emerald-500" />
                                    ) : (
                                        <Clock size={14} className="text-amber-500" />
                                    )}
                                </div>
                                <span className="text-[10px] text-slate-400 font-medium">
                                    {new Date(typeof counsel.date === 'number' ? counsel.date : (counsel.date as any).toDate ? (counsel.date as any).toDate() : counsel.date).toLocaleDateString()}
                                </span>
                            </div>

                            <p className="text-sm text-slate-500 line-clamp-2 mb-3 leading-relaxed">
                                {counsel.content}
                            </p>

                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black ${selectedId === counsel.id ? 'bg-indigo-200 text-indigo-700' : 'bg-slate-100 text-slate-500'}`}>
                                        <User size={12} />
                                    </div>
                                    <span className="text-xs font-bold text-slate-600">
                                        {getMemberName(counsel.memberId)}
                                        <span className="text-slate-400 font-normal ml-1">({getMemberGrade(counsel.memberId)})</span>
                                    </span>
                                </div>
                                <span className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded-md font-bold">
                                    {counsel.tag}
                                </span>
                            </div>
                        </div>
                    ))}
                    {paginatedCounsels.length === 0 && (
                        <div className="p-10 text-center text-slate-400">
                            <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3">
                                <Search size={20} />
                            </div>
                            <p className="text-sm">상담 내역이 없습니다.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Pagination Controls */}
            <div className="border-t border-slate-100 bg-white">
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
            </div>
        </div>
    );
};

export default CounselList;
